function getEnquiryPrice(property) {
  const candidates = [
    property?.cheapestRoomPrice,
    property?.price,
    property?.cheapest_room_price,
  ];

  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === '') {
      continue;
    }

    const value =
      typeof candidate === 'string'
        ? Number(candidate.replace(/,/g, ''))
        : Number(candidate);

    if (Number.isFinite(value)) {
      return value;
    }
  }

  return Number.MAX_SAFE_INTEGER;
}

function getEnquiryThumbnail(property) {
  if (!property) {
    return null;
  }

  return (
    (typeof property?.mainImage === 'string' ? property.mainImage : null) ||
    property?.mainImage?.src?.medium?.url ||
    property?.mainImage?.src?.small?.url ||
    property?.mainImage?.src?.tablet?.url ||
    property?.main_image_tablet_url ||
    property?.mainImageTabletUrl ||
    property?.defaultImage ||
    null
  );
}

function getPropertyCityLabel(property) {
  if (!property) {
    return '';
  }

  const explicitCity =
    property?.city ||
    property?.cityName ||
    property?.area?.city?.name ||
    property?.areaCityName ||
    '';

  if (String(explicitCity || '').trim()) {
    return String(explicitCity).trim();
  }

  const path = property?.path;
  if (!path) {
    return '';
  }

  try {
    const parsed = new URL(String(path), window.location.origin);
    const segments = parsed.pathname.split('/').filter(Boolean);
    const citySlugIndex = segments.indexOf('student-accommodation');

    if (citySlugIndex === -1 || !segments[citySlugIndex + 1]) {
      return '';
    }

    return segments[citySlugIndex + 1]
      .split('-')
      .map((part) =>
        part.length ? part.charAt(0).toUpperCase() + part.slice(1) : ''
      )
      .join(' ')
      .trim();
  } catch (_error) {
    return '';
  }
}

function buildEnquiryCarouselCard(property) {
  const price = getEnquiryPrice(property);
  const hasPrice =
    Number.isFinite(price) && price !== Number.MAX_SAFE_INTEGER;
  const address = String(
    property?.addressShort || property?.address || ''
  ).trim();

  return {
    url: property?.path || '#',
    thumbnail: getEnquiryThumbnail(property),
    alt: address,
    address,
    beds: property?.roomsAvailableCount ?? '',
    baths: property?.bathroomCount ?? '',
    from: Boolean(property?.showFrom ?? property?.propertyPriceFrom),
    price: hasPrice ? price : '',
  };
}

/**
 * Update the ids of properties requiring a full name on the enquiry modal name
 * input, using the advert types configured in `data-full-name-advert-types`.
 * Read by `FormValidation.shortlistRequiresFullName` (req_full_name_shortlist).
 */
function syncEnquiryModalFullNameProperties(properties) {
  const nameInput = document.querySelector(
    'input[name="name"][data-full-name-advert-types]'
  );

  if (!(nameInput instanceof HTMLInputElement)) {
    return;
  }

  let advertTypes = [];

  try {
    advertTypes = JSON.parse(nameInput.dataset.fullNameAdvertTypes || '[]');
  } catch (_error) {
    advertTypes = [];
  }

  const requiredIds = properties
    .filter((property) => {
      const advertType = String(
        property?.advertType ?? property?.advert_type ?? ''
      );

      return advertType !== '' && advertTypes.includes(advertType);
    })
    .map((property) => String(property?.id || '').trim())
    .filter(Boolean);

  nameInput.dataset.fullNamePropertyIds = JSON.stringify(requiredIds);
}

/**
 * Keep the global enquiry modal summary, carousel, and hidden shortlist IDs in
 * sync with the current selected shortlist properties.
 */
export function syncEnquiryModalShortlistSummary(enquiryProperties = []) {
  if (typeof document === 'undefined') {
    return 0;
  }

  const properties = Array.isArray(enquiryProperties) ? enquiryProperties : [];
  const enquiryCount = properties.length;

  const countEl = document.getElementById('enquiry_multi_property_count_text');
  const locationRowEl = document.getElementById('enquiry_multi_location_row');
  const citiesEl = document.getElementById('enquiry_multi_cities_text');
  const lowestPriceEl = document.getElementById(
    'enquiry_multi_lowest_price_text'
  );
  const thumbnailItems = Array.from(
    document.querySelectorAll('[data-enquiry-thumbnail-item]')
  );
  const thumbnailImages = Array.from(
    document.querySelectorAll('[data-enquiry-thumbnail-image]')
  );
  const singleThumbnail = document.getElementById(
    'enquiry_multi_single_thumbnail'
  );
  const carouselEl = document.querySelector('[data-uh="enquiry-modal-carousel"]');
  const shortlistPropertyIdsInput = document.getElementById(
    'enquiry_shortlist_property_ids'
  );

  if (
    !countEl &&
    !locationRowEl &&
    !citiesEl &&
    !lowestPriceEl &&
    thumbnailItems.length === 0 &&
    !(singleThumbnail instanceof HTMLImageElement) &&
    !carouselEl &&
    !(shortlistPropertyIdsInput instanceof HTMLInputElement) &&
    (!window.EnquiryModalCarousel ||
      typeof window.EnquiryModalCarousel.render !== 'function')
  ) {
    return enquiryCount;
  }

  const propertyIds = properties
    .map((property) => String(property?.id || '').trim())
    .filter(Boolean);

  if (shortlistPropertyIdsInput instanceof HTMLInputElement) {
    shortlistPropertyIdsInput.value = propertyIds.join(',');
  }

  syncEnquiryModalFullNameProperties(properties);

  const lowestPrice = properties.reduce((carry, property) => {
    const price = getEnquiryPrice(property);

    if (!Number.isFinite(price) || price === Number.MAX_SAFE_INTEGER) {
      return carry;
    }

    if (!Number.isFinite(carry)) {
      return price;
    }

    return Math.min(carry, price);
  }, NaN);

  const citySummary = properties
    .map((property) => getPropertyCityLabel(property))
    .map((city) => String(city || '').trim())
    .filter((city, index, arr) => city && arr.indexOf(city) === index)
    .join(', ');

  const firstProperty = properties[0] || null;
  const thumbnails = properties.map((property) => getEnquiryThumbnail(property));

  if (thumbnailItems.length > 0) {
    thumbnailItems.forEach((item, index) => {
      const image = thumbnailImages[index];
      const thumbnail = thumbnails[index];
      const shouldShow = index < enquiryCount;

      item.classList.toggle('tw-hidden', !shouldShow);

      if (image && shouldShow) {
        image.setAttribute(
          'src',
          thumbnail || image.getAttribute('data-enquiry-thumbnail-placeholder') || ''
        );
      }
    });
  }

  if (singleThumbnail instanceof HTMLImageElement) {
    singleThumbnail.setAttribute(
      'src',
      thumbnails[0] ||
        singleThumbnail.getAttribute('data-enquiry-thumbnail-placeholder') ||
        ''
    );
  }

  const singlePropertyLocation = firstProperty
    ? String(
        firstProperty?.addressShort ||
          firstProperty?.address ||
          firstProperty?.fullAddress ||
          [
            firstProperty?.area?.name,
            firstProperty?.area?.city?.name,
            firstProperty?.city,
          ]
            .filter(Boolean)
            .join(', ')
      ).trim()
    : '';

  const locationText =
    enquiryCount === 1 ? singlePropertyLocation : citySummary;

  if (countEl) {
    countEl.textContent = enquiryCount
      ? `${enquiryCount} ${enquiryCount === 1 ? 'Property' : 'Properties'}`
      : '';
  }

  if (citiesEl) {
    citiesEl.textContent = locationText;
  }

  if (locationRowEl) {
    locationRowEl.classList.toggle('tw-hidden', !locationText);
  }

  if (lowestPriceEl) {
    lowestPriceEl.textContent = Number.isFinite(lowestPrice)
      ? String(Math.round(lowestPrice))
      : '';
  }

  if (
    window.EnquiryModalCarousel &&
    typeof window.EnquiryModalCarousel.render === 'function'
  ) {
    window.EnquiryModalCarousel.render(
      properties.map((property) => buildEnquiryCarouselCard(property))
    );
  }

  return enquiryCount;
}
